import React, { useState, useCallback } from 'react'
import styled from '@emotion/styled'
import { components, type OptionProps } from 'react-select'
import { CheckboxBase, spacing, ChevronUpIcon, ChevronDownIcon, Text } from '@instacart/ids-core'
import {
  type GroupedOptions,
  OptionType,
  type RetailerLocationOption,
  type StoreGroupOption,
} from 'domains/account-management/components/retailer-location-store-group-select/utils/types'

interface OptionContainerProps {
  showBorder: boolean
}

const OptionContainer = styled.div<OptionContainerProps>(({ theme, showBorder }) => ({
  borderBottom: showBorder ? `1px solid ${theme.colors.systemGrayscale20}` : 'none',
}))

const OptionHeader = styled.div(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'flex-start',
  alignItems: 'center',
  paddingLeft: spacing.s24,
  ...theme.typography.bodyMedium1,
}))

const RetailerLocationsContainer = styled.div({
  paddingLeft: '84px',
})

const LocationContainer = styled.div({
  height: '38px',
  padding: `${spacing.s8}px 0`,
})

const ExpandToggle = styled.button({
  display: 'flex',
  background: 'transparent',
  marginLeft: spacing.s8,
})

export const Option = (props: OptionProps<GroupedOptions>) => {
  const [expanded, setExpanded] = useState<boolean>(false)
  const { type, value } = props.data as StoreGroupOption | RetailerLocationOption
  const retailerLocations = (props.data as StoreGroupOption)?.retailerLocations
  const checkboxId = `${type}-checkbox-${value}`
  const selectedValues = props.getValue() as unknown as GroupedOptions['options']
  const isSelected = selectedValues
    ? selectedValues.some(option => option.value === value && option.type === type)
    : false

  const expandedIcon = expanded ? (
    <ChevronDownIcon color="systemGrayscale30" size={20} />
  ) : (
    <ChevronUpIcon color="systemGrayscale30" size={20} />
  )

  const handleExpandToggleClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      e.stopPropagation()
      setExpanded(!expanded)
    },
    [expanded]
  )

  const isStoreGroup = type === OptionType.StoreGroup

  return (
    <OptionContainer showBorder={isStoreGroup}>
      <components.Option {...props}>
        <OptionHeader>
          <CheckboxBase checked={isSelected} id={checkboxId} />
          <Text
            typography={isStoreGroup ? 'subtitleMedium' : 'bodyMedium1'}
            color={isSelected ? 'systemGrayscale70' : 'systemGrayscale50'}
          >
            {props.children}
            {isStoreGroup && ` (${retailerLocations.length})`}
          </Text>
          {isStoreGroup && (
            <ExpandToggle onClick={handleExpandToggleClick}>{expandedIcon}</ExpandToggle>
          )}
        </OptionHeader>
      </components.Option>
      {isStoreGroup && expanded && (
        <RetailerLocationsContainer>
          {retailerLocations.map(location => (
            <LocationContainer key={location.id}>
              <Text
                typography="subtitleMedium"
                color={isSelected ? 'systemGrayscale70' : 'systemGrayscale50'}
              >
                {location.name}
              </Text>
            </LocationContainer>
          ))}
        </RetailerLocationsContainer>
      )}
    </OptionContainer>
  )
}
